import client from "services/ngage/api-client";
import {Greeting, GreetingData, GreetingPreview} from "types/Greeting";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";
import deserializeGreeting from "./deserializers/greeting";

type GreetingFilters = {
    search?: string | null
    enabled?: boolean
    page?: number
    perPage?: number
}

/**
 * Get all greetings that match the given filters (paginated)
 */
const filtered = async (filters: GreetingFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<GreetingPreview> | null>('/greetings', {params})).data

        if (page) page.data = page.data.map(deserializeGreeting)

        return page
    } catch (error) {
        console.error("Unable to fetch greetings", error)
        return null
    }
}

/**
 * Get a single greeting
 *
 * @param {number} greetingId
 */
const get = async (greetingId: number) => {
    try {
        const greeting = (await client.get<Greeting | null>(`/greetings/${greetingId}`)).data
        return greeting ? deserializeGreeting(greeting) : null
    } catch (error) {
        console.error(`Unable to fetch greeting ${greetingId}`, error)
        return null
    }
}

/**
 * Get the current greeting
 */
const current = async () => {
    try {
        const greeting = (await client.get<Greeting | null>('/greetings/current')).data
        return greeting ? deserializeGreeting(greeting) : null
    } catch (error) {
        console.error('Unable to fetch current greeting', error)
        return null
    }
}

/**
 * Store a new greeting
 *
 * @param data
 */
const store = async (data: GreetingData) => {
    try {
        const greeting = (await client.post<Greeting | null>('/greetings', data)).data
        return greeting ? deserializeGreeting(greeting) : null
    } catch (error) {
        console.error("Unable to store greeting", error)
        throw error
    }
}

/**
 * Update an existing greeting
 *
 * @param {number} greetingId
 * @param data
 */
const update = async (greetingId: number, data: GreetingData) => {
    try {
        const greeting = (await client.put<Greeting | null>(`/greetings/${greetingId}`, data)).data
        return greeting ? deserializeGreeting(greeting) : null
    } catch (error) {
        console.error(`Unable to update greeting ${greetingId}`, error)
        throw error
    }
}

/**
 * Permanently delete a greeting
 *
 * @param {number} greetingId
 */
const destroy = async (greetingId: number) => {
    try {
        return (await client.delete<null>(`/greetings/${greetingId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete greeting ${greetingId}`, error)
        throw error
    }
}

const greetings = {
    filtered,
    get,
    current,
    store,
    update,
    destroy
}

export default greetings;